/**
 * Сообщить о сбое на сервер (перенос GiperUI/src/lib/crashReport.ts).
 *
 * 🔴 Зачем: сбой жил только в телефоне человека и исчезал с перезапуском —
 * жалоба превращалась в гадание (урок истории с Safari у Бехзода). Теперь
 * каждый пойманный сбой уходит в ГиперТим, виден в админке («Сбои у людей») и
 * тревогой в Телеграм.
 *
 * Отличия от оболочки: пропуск берём у библиотеки входа (НЕ из localStorage
 * 'token'), раздел — `saleus/<маршрут>`, сборка — штамп приложения.
 * Отправка никогда не бросает ошибку; один и тот же сбой — не чаще раза в минуту.
 */
import config from './config';
import { getFreshAccessToken } from './authToken';
import { BUILD_STAMP } from './buildInfo';
import { useProjectStore } from '@/store/project';

export type ВидСбоя = 'section' | 'widget' | 'window' | 'promise';

export interface Сбой {
  kind: ВидСбоя;
  error: unknown;
  componentStack?: string | null;
  /** Раздел; если не задан — по текущему маршруту. */
  section?: string | null;
}

const недавно = new Map<string, number>();
const ОКНО_ПОВТОРА_МС = 60_000;

/** Для проверок: забыть, что уже отправляли. */
export function resetCrashDedupe(): void {
  недавно.clear();
}

/** Шум, по которому чинить нечего: обрывы сети и служебные сообщения браузера. */
export function isNoise(text: string): boolean {
  return (
    text === 'Script error.' ||
    text.includes('ResizeObserver loop') ||
    text.includes('AbortError') ||
    text.includes('The user aborted a request') ||
    text === 'Failed to fetch' ||
    text === 'Load failed' ||
    text.includes('NetworkError when attempting to fetch')
  );
}

function currentSection(): string {
  const first = window.location.pathname.split('/')[1] || 'root';
  return `saleus/${first}`.slice(0, 60);
}

export function reportCrash({ kind, error, componentStack, section }: Сбой): void {
  try {
    const err = error as { message?: unknown; stack?: unknown; name?: unknown } | null;
    const message = String(err?.message ?? error ?? '').slice(0, 1000) || String(err?.name ?? 'Ошибка без текста');
    if (isNoise(message)) return;

    const раздел = section ?? currentSection();
    const ключ = `${kind}|${раздел}|${message}`;
    const now = Date.now();
    // Склейка повторов — СИНХРОННО, до любых await: иначе два одинаковых
    // сбоя подряд оба успели бы пройти.
    if (now - (недавно.get(ключ) ?? 0) < ОКНО_ПОВТОРА_МС) return;
    недавно.set(ключ, now);

    void (async () => {
      try {
        const token = await getFreshAccessToken();
        if (!token) return; // до входа сообщать некому и не от чьего имени
        const st = useProjectStore.getState();
        const orgId = st.projects.find((p) => p.id === st.activeProjectId)?.org_id;
        await fetch(`${config.TEAM_API_URL}/api/client-errors`, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...(orgId ? { 'X-Org-Id': orgId } : {}),
          },
          body: JSON.stringify({
            kind,
            message,
            stack: typeof err?.stack === 'string' ? err.stack.slice(0, 8000) : undefined,
            componentStack: componentStack ? componentStack.slice(0, 4000) : undefined,
            section: раздел,
            url: (window.location.pathname + window.location.search).slice(0, 500),
            build: BUILD_STAMP,
          }),
        });
      } catch { /* сбор сбоев не должен ронять страницу */ }
    })();
  } catch { /* то же */ }
}

/** Подключить сбор сбоев вне React: ошибки окна и необработанные обещания. */
export function installCrashReporting(): void {
  window.addEventListener('error', (e) => {
    reportCrash({ kind: 'window', error: e.error ?? e.message });
  });
  window.addEventListener('unhandledrejection', (e) => {
    reportCrash({ kind: 'promise', error: e.reason });
  });
}
